// src/brands/currency.enum.ts
var Currency = /* @__PURE__ */ ((Currency2) => {
  Currency2["EUR"] = "EUR";
  Currency2["CHF"] = "CHF";
  Currency2["MAD"] = "MAD";
  return Currency2;
})(Currency || {});
var CURRENCY_INFO = {
  ["EUR" /* EUR */]: {
    code: "EUR" /* EUR */,
    name: "Euro",
    symbol: "\u20AC",
    locale: "fr-FR"
  },
  ["CHF" /* CHF */]: {
    code: "CHF" /* CHF */,
    name: "Franc suisse",
    symbol: "CHF",
    locale: "fr-CH"
  },
  ["MAD" /* MAD */]: {
    code: "MAD" /* MAD */,
    name: "Dirham marocain",
    symbol: "MAD",
    locale: "fr-MA"
  }
};
var SUPPORTED_CURRENCIES = Object.values(Currency);
var DEFAULT_CURRENCY = "EUR" /* EUR */;

// src/auth/auth.validators.ts
import { z } from "zod";
var passwordRules = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true
};
var passwordSchema = z.string().min(passwordRules.minLength, `Le mot de passe doit contenir au moins ${passwordRules.minLength} caracteres`).regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule").regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule").regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre");
var loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis")
});
var forgotPasswordSchema = z.object({
  email: z.string().email("Email invalide")
});
var setPasswordSchema = z.object({
  token: z.string().min(1, "Token requis"),
  password: passwordSchema
});
var validateTokenSchema = z.object({
  token: z.string().min(1, "Token requis")
});

// src/auth/current-currency.ts
function resolveCurrentCurrency(user) {
  if (!user) return "EUR" /* EUR */;
  const brandId = user.currentBrand?.id;
  const company = user.companies?.find((c) => c.brands.some((b) => b.id === brandId));
  return company?.currency ?? user.company?.currency ?? "EUR" /* EUR */;
}

// src/users/user-role.enum.ts
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["OWNER"] = "OWNER";
  UserRole2["ADMIN"] = "ADMIN";
  UserRole2["MANAGER"] = "MANAGER";
  UserRole2["OPERATOR"] = "OPERATOR";
  UserRole2["USER"] = "OPERATOR";
  UserRole2["VIEWER"] = "VIEWER";
  return UserRole2;
})(UserRole || {});
var RoleHierarchy = {
  ["OWNER" /* OWNER */]: 80,
  ["ADMIN" /* ADMIN */]: 60,
  ["MANAGER" /* MANAGER */]: 50,
  ["OPERATOR" /* OPERATOR */]: 40,
  ["VIEWER" /* VIEWER */]: 20
};
function hasMinimumRole(userRole, requiredRole) {
  return RoleHierarchy[userRole] >= RoleHierarchy[requiredRole];
}

// src/users/user.types.ts
var ActivationStatus = /* @__PURE__ */ ((ActivationStatus2) => {
  ActivationStatus2["ACTIVE"] = "ACTIVE";
  ActivationStatus2["INACTIVE"] = "INACTIVE";
  ActivationStatus2["PENDING"] = "PENDING";
  return ActivationStatus2;
})(ActivationStatus || {});
var TokenType = /* @__PURE__ */ ((TokenType2) => {
  TokenType2["ACTIVATION"] = "ACTIVATION";
  TokenType2["PASSWORD_RESET"] = "PASSWORD_RESET";
  return TokenType2;
})(TokenType || {});

// src/users/user.validators.ts
import { z as z2 } from "zod";

// src/products/product.enum.ts
var Product = /* @__PURE__ */ ((Product3) => {
  Product3["BSTOCK"] = "BSTOCK";
  Product3["BSTAFF"] = "BSTAFF";
  Product3["BDISPLAY"] = "BDISPLAY";
  return Product3;
})(Product || {});
var ALL_PRODUCTS = [
  "BSTOCK" /* BSTOCK */,
  "BSTAFF" /* BSTAFF */,
  "BDISPLAY" /* BDISPLAY */
];
var PRODUCT_LABELS = {
  ["BSTOCK" /* BSTOCK */]: "BStock",
  ["BSTAFF" /* BSTAFF */]: "BStaff",
  ["BDISPLAY" /* BDISPLAY */]: "BDisplay"
};

// src/users/user.validators.ts
var createUserSchema = z2.object({
  email: z2.string().email("Email invalide"),
  firstName: z2.string().min(2, "Le prenom doit contenir au moins 2 caracteres"),
  lastName: z2.string().min(2, "Le nom doit contenir au moins 2 caracteres"),
  role: z2.nativeEnum(UserRole),
  /**
   * Module d'origine de la creation (US-MAC.8). Si fourni, seul le role de
   * ce module est pose ; les 2 autres colonnes restent null.
   */
  module: z2.nativeEnum(Product).optional()
});
var updateUserSchema = z2.object({
  firstName: z2.string().min(2, "Le prenom doit contenir au moins 2 caracteres").optional(),
  lastName: z2.string().min(2, "Le nom doit contenir au moins 2 caracteres").optional(),
  isActive: z2.boolean().optional()
});
var updateUserRoleSchema = z2.object({
  role: z2.nativeEnum(UserRole)
});
var getUsersQuerySchema = z2.object({
  search: z2.string().optional(),
  role: z2.nativeEnum(UserRole).optional(),
  status: z2.enum(["active", "inactive", "pending"]).optional(),
  page: z2.coerce.number().int().positive().default(1),
  limit: z2.coerce.number().int().positive().max(100).default(20)
});
var createUserWithPasswordSchema = z2.object({
  email: z2.string().email("Email invalide"),
  password: z2.string().min(8, "Le mot de passe doit contenir au moins 8 caracteres"),
  firstName: z2.string().min(2, "Le prenom doit contenir au moins 2 caracteres"),
  lastName: z2.string().min(2, "Le nom doit contenir au moins 2 caracteres"),
  role: z2.nativeEnum(UserRole),
  brandIds: z2.array(z2.string().uuid()).optional()
});

// src/brands/brand-type.enum.ts
var BrandType = {
  GROUP: "GROUP",
  POINT_OF_SALE: "POINT_OF_SALE",
  CENTRAL: "CENTRAL"
};

// src/brands/brand.validators.ts
import { z as z3 } from "zod";
var createBrandSchema = z3.object({
  name: z3.string().min(2, "Le nom du brand doit contenir au moins 2 caracteres"),
  type: z3.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]),
  parentId: z3.string().cuid().optional()
});
var updateBrandSchema = z3.object({
  name: z3.string().min(2).optional(),
  type: z3.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]).optional(),
  isActive: z3.boolean().optional(),
  parentId: z3.string().cuid().optional()
});
var updateBrandSettingsSchema = z3.object({
  name: z3.string().min(2, "Le nom doit contenir au moins 2 caracteres").optional(),
  address: z3.string().min(1).nullable().optional(),
  city: z3.string().min(1).nullable().optional(),
  postalCode: z3.string().min(1).nullable().optional(),
  phone: z3.string().min(1).nullable().optional(),
  email: z3.string().email("Adresse email invalide").nullable().optional(),
  legalId: z3.string().min(1).nullable().optional(),
  cutoffHour: z3.number().int().min(0).max(23).optional()
});
var updateBrandPayrollSettingsSchema = z3.object({
  fullTimeHours: z3.number().positive().max(168).optional(),
  overtime25MaxHours: z3.number().min(0).max(168).optional(),
  nightStartHour: z3.number().int().min(0).max(24).optional(),
  nightEndHour: z3.number().int().min(0).max(24).optional()
}).refine((data) => data.nightStartHour === void 0 || data.nightEndHour === void 0 || data.nightStartHour !== data.nightEndHour, {
  message: "L'heure de debut et de fin ne peuvent pas etre identiques",
  path: ["nightEndHour"]
});

// src/brands/company.validators.ts
import { z as z4 } from "zod";
var createCompanySchema = z4.object({
  name: z4.string().min(2, "Le nom de l'entreprise doit contenir au moins 2 caracteres")
});
var updateCompanySchema = z4.object({
  name: z4.string().min(2, "Le nom de l'entreprise doit contenir au moins 2 caracteres").optional()
});

// src/brands/admin.validators.ts
import { z as z5 } from "zod";
var createAdminCompanySchema = z5.object({
  name: z5.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  country: z5.string().max(100, "Pays: maximum 100 caracteres").optional().default("FR"),
  currency: z5.nativeEnum(Currency).optional().default("EUR" /* EUR */)
});
var updateAdminCompanySchema = z5.object({
  name: z5.string().min(2).max(200).optional(),
  country: z5.string().max(100).optional(),
  currency: z5.nativeEnum(Currency).optional(),
  isActive: z5.boolean().optional(),
  primaryColor: z5.string().regex(/^#[0-9A-Fa-f]{6}$/, "Couleur invalide (format: #RRGGBB)").nullish().transform((val) => val || null)
});
var timezoneSchema = z5.string().max(50).regex(/^[A-Za-z_\/]+$/, "Timezone invalide (format: Area/Location)");
var createAdminBrandSchema = z5.object({
  name: z5.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  type: z5.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]),
  parentId: z5.string().uuid("ID parent invalide").optional(),
  timezone: timezoneSchema.optional().default("Europe/Paris")
}).refine(
  (data) => {
    if ((data.type === "POINT_OF_SALE" || data.type === "CENTRAL") && !data.parentId) {
      return false;
    }
    if (data.type === "GROUP" && data.parentId) {
      return false;
    }
    return true;
  },
  {
    message: "Un SITE/CENTRAL doit avoir un parent GROUP, un GROUP ne peut pas avoir de parent",
    path: ["parentId"]
  }
);
var updateAdminBrandSchema = z5.object({
  name: z5.string().min(2).max(200).optional(),
  isActive: z5.boolean().optional(),
  timezone: timezoneSchema.optional()
});
var updateCompanyBrandingSchema = z5.object({
  primaryColor: z5.string().regex(/^#[0-9A-Fa-f]{6}$/, "Couleur primaire: format hexadecimal requis (ex: #FF5733)").nullable().optional()
});

// src/audit/audit.validators.ts
import { z as z7 } from "zod";

// src/common/date.validators.ts
import { z as z6 } from "zod";
var dateOrDateTimeString = z6.string().regex(
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/,
  "Date invalide (format: YYYY-MM-DD ou YYYY-MM-DDTHH:mm)"
);
var isoDateTimeString = z6.string().datetime("Date invalide (format ISO 8601 attendu)");

// src/audit/audit.validators.ts
var auditActionEnum = z7.enum([
  "CREATE",
  "UPDATE",
  "DELETE",
  "VALIDATE",
  "CANCEL",
  "LOGIN",
  "LOGOUT"
]);
var appSourceEnum = z7.enum(["BSTOCK", "BSTAFF", "BDISPLAY", "CORE"]);
var queryAuditLogsSchema = z7.object({
  app: appSourceEnum.optional(),
  actorId: z7.string().uuid().optional(),
  entityType: z7.string().optional(),
  entityId: z7.string().uuid().optional(),
  eventType: z7.string().optional(),
  action: auditActionEnum.optional(),
  dateFrom: dateOrDateTimeString.optional(),
  dateTo: dateOrDateTimeString.optional(),
  search: z7.string().optional(),
  page: z7.coerce.number().int().positive().default(1),
  limit: z7.coerce.number().int().positive().max(100).default(50)
});
var queryEntityHistorySchema = z7.object({
  page: z7.coerce.number().int().positive().default(1),
  limit: z7.coerce.number().int().positive().max(100).default(50)
});
var queryAuditStatsSchema = z7.object({
  dateFrom: dateOrDateTimeString.optional(),
  dateTo: dateOrDateTimeString.optional()
});

// src/permissions/permissions.ts
var PERMISSION_MATRIX = {
  "stock.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "stock.read_financials": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "inventories.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "inventories.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "inventories.edit": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "inventories.validate": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "inventories.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "goods-receipts.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "goods-receipts.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "goods-receipts.validate": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "goods-receipts.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "purchase-orders.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "purchase-orders.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "purchase-orders.send": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "purchase-orders.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "losses.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "losses.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "losses.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "transfers.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "transfers.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "transfers.ship": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "transfers.receive": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "transfers.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "supplier-returns.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "supplier-returns.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "supplier-returns.validate": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "supplier-returns.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "raw-products.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "raw-products.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "raw-products.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "pos-products.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "pos-products.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "pos-products.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "recipes.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "recipes.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "recipes.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "reports.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "VIEWER" /* VIEWER */],
  "reports.read_financials": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "users.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "users.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "users.edit": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "users.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "settings.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "settings.edit": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "suppliers.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "suppliers.create": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "suppliers.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "audit.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "pos-sales.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "pos-sales.sync": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  // === BStaff ===
  "employees.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "employees.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "employees.delete": ["OWNER" /* OWNER */],
  "clock-entries.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "clock-entries.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "clock-entries.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "contracts.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "contracts.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "attendance.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "analytics.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "kiosk.configure": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  "absences.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "absences.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  // === BDisplay (legacy grossier — deprecated) ===
  "bdisplay.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "bdisplay.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "bdisplay.admin": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */],
  // === BDisplay (granularite fine US-MAC.5) ===
  "media.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "media.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "media.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "screens.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "screens.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "screens.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "screens.remote.read": ["OWNER" /* OWNER */],
  "screens.remote.write": ["OWNER" /* OWNER */],
  "playlists.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "playlists.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "playlists.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "programmations.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "programmations.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */],
  "programmations.delete": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "tags.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "tags.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */],
  "menu-boards.read": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */, "OPERATOR" /* OPERATOR */, "VIEWER" /* VIEWER */],
  "menu-boards.write": ["OWNER" /* OWNER */, "ADMIN" /* ADMIN */, "MANAGER" /* MANAGER */]
};
function hasPermission(role, permission) {
  const allowedRoles = PERMISSION_MATRIX[permission];
  if (!allowedRoles) return false;
  return allowedRoles.includes(role);
}

// src/permissions/module-permissions.ts
var PERMISSION_TO_PRODUCT = {
  // ===========================================================================
  // BStock
  // ===========================================================================
  "stock.read": "BSTOCK" /* BSTOCK */,
  "stock.read_financials": "BSTOCK" /* BSTOCK */,
  "inventories.read": "BSTOCK" /* BSTOCK */,
  "inventories.create": "BSTOCK" /* BSTOCK */,
  "inventories.edit": "BSTOCK" /* BSTOCK */,
  "inventories.validate": "BSTOCK" /* BSTOCK */,
  "inventories.delete": "BSTOCK" /* BSTOCK */,
  "goods-receipts.read": "BSTOCK" /* BSTOCK */,
  "goods-receipts.create": "BSTOCK" /* BSTOCK */,
  "goods-receipts.validate": "BSTOCK" /* BSTOCK */,
  "goods-receipts.delete": "BSTOCK" /* BSTOCK */,
  "purchase-orders.read": "BSTOCK" /* BSTOCK */,
  "purchase-orders.create": "BSTOCK" /* BSTOCK */,
  "purchase-orders.send": "BSTOCK" /* BSTOCK */,
  "purchase-orders.delete": "BSTOCK" /* BSTOCK */,
  "losses.read": "BSTOCK" /* BSTOCK */,
  "losses.create": "BSTOCK" /* BSTOCK */,
  "losses.delete": "BSTOCK" /* BSTOCK */,
  "transfers.read": "BSTOCK" /* BSTOCK */,
  "transfers.create": "BSTOCK" /* BSTOCK */,
  "transfers.ship": "BSTOCK" /* BSTOCK */,
  "transfers.receive": "BSTOCK" /* BSTOCK */,
  "transfers.delete": "BSTOCK" /* BSTOCK */,
  "supplier-returns.read": "BSTOCK" /* BSTOCK */,
  "supplier-returns.create": "BSTOCK" /* BSTOCK */,
  "supplier-returns.validate": "BSTOCK" /* BSTOCK */,
  "supplier-returns.delete": "BSTOCK" /* BSTOCK */,
  "raw-products.read": "BSTOCK" /* BSTOCK */,
  "raw-products.create": "BSTOCK" /* BSTOCK */,
  "raw-products.delete": "BSTOCK" /* BSTOCK */,
  "pos-products.read": "BSTOCK" /* BSTOCK */,
  "pos-products.create": "BSTOCK" /* BSTOCK */,
  "pos-products.delete": "BSTOCK" /* BSTOCK */,
  "recipes.read": "BSTOCK" /* BSTOCK */,
  "recipes.create": "BSTOCK" /* BSTOCK */,
  "recipes.delete": "BSTOCK" /* BSTOCK */,
  "reports.read": "BSTOCK" /* BSTOCK */,
  "reports.read_financials": "BSTOCK" /* BSTOCK */,
  "suppliers.read": "BSTOCK" /* BSTOCK */,
  "suppliers.create": "BSTOCK" /* BSTOCK */,
  "suppliers.delete": "BSTOCK" /* BSTOCK */,
  "pos-sales.read": "BSTOCK" /* BSTOCK */,
  "pos-sales.sync": "BSTOCK" /* BSTOCK */,
  // ===========================================================================
  // BStaff
  // ===========================================================================
  "employees.read": "BSTAFF" /* BSTAFF */,
  "employees.write": "BSTAFF" /* BSTAFF */,
  "employees.delete": "BSTAFF" /* BSTAFF */,
  "clock-entries.read": "BSTAFF" /* BSTAFF */,
  "clock-entries.write": "BSTAFF" /* BSTAFF */,
  "clock-entries.delete": "BSTAFF" /* BSTAFF */,
  "contracts.read": "BSTAFF" /* BSTAFF */,
  "contracts.write": "BSTAFF" /* BSTAFF */,
  "attendance.read": "BSTAFF" /* BSTAFF */,
  "analytics.read": "BSTAFF" /* BSTAFF */,
  "kiosk.configure": "BSTAFF" /* BSTAFF */,
  "absences.read": "BSTAFF" /* BSTAFF */,
  "absences.write": "BSTAFF" /* BSTAFF */,
  // ===========================================================================
  // BDisplay
  // ===========================================================================
  // Legacy grossier (deprecated, retro-compat)
  "bdisplay.read": "BDISPLAY" /* BDISPLAY */,
  "bdisplay.write": "BDISPLAY" /* BDISPLAY */,
  "bdisplay.admin": "BDISPLAY" /* BDISPLAY */,
  // Granularite fine (US-MAC.5)
  "media.read": "BDISPLAY" /* BDISPLAY */,
  "media.write": "BDISPLAY" /* BDISPLAY */,
  "media.delete": "BDISPLAY" /* BDISPLAY */,
  "screens.read": "BDISPLAY" /* BDISPLAY */,
  "screens.write": "BDISPLAY" /* BDISPLAY */,
  "screens.delete": "BDISPLAY" /* BDISPLAY */,
  "screens.remote.read": "BDISPLAY" /* BDISPLAY */,
  "screens.remote.write": "BDISPLAY" /* BDISPLAY */,
  "playlists.read": "BDISPLAY" /* BDISPLAY */,
  "playlists.write": "BDISPLAY" /* BDISPLAY */,
  "playlists.delete": "BDISPLAY" /* BDISPLAY */,
  "programmations.read": "BDISPLAY" /* BDISPLAY */,
  "programmations.write": "BDISPLAY" /* BDISPLAY */,
  "programmations.delete": "BDISPLAY" /* BDISPLAY */,
  "tags.read": "BDISPLAY" /* BDISPLAY */,
  "tags.write": "BDISPLAY" /* BDISPLAY */,
  // Anticipation Phase 11
  "menu-boards.read": "BDISPLAY" /* BDISPLAY */,
  "menu-boards.write": "BDISPLAY" /* BDISPLAY */
  // ===========================================================================
  // Transverses (NON listees ici — accessibles avec n'importe quel module) :
  //   'users.read', 'users.create', 'users.edit', 'users.delete',
  //   'settings.read', 'settings.edit',
  //   'audit.read'
  // ===========================================================================
};
var MODULE_PERMISSIONS = (() => {
  const map = {
    ["BSTOCK" /* BSTOCK */]: [],
    ["BSTAFF" /* BSTAFF */]: [],
    ["BDISPLAY" /* BDISPLAY */]: []
  };
  for (const [perm, product] of Object.entries(PERMISSION_TO_PRODUCT)) {
    if (product) map[product].push(perm);
  }
  return map;
})();

// src/permissions/access.ts
function isAdminRole(role) {
  return role === "ADMIN" /* ADMIN */ || role === "OWNER" /* OWNER */;
}
function getAccessibleProducts(permissions, role) {
  if (isAdminRole(role)) {
    return [...ALL_PRODUCTS];
  }
  const products = /* @__PURE__ */ new Set();
  for (const perm of permissions) {
    const product = PERMISSION_TO_PRODUCT[perm];
    if (product) products.add(product);
  }
  return Array.from(products);
}
function hasModuleAccess(permissions, role, product) {
  if (isAdminRole(role)) return true;
  return MODULE_PERMISSIONS[product].some((p) => permissions.includes(p));
}
function canAccessModule(input) {
  if (!input.brandSubscribedProducts.includes(input.product)) return false;
  return hasModuleAccess(input.userPermissions, input.userRole, input.product);
}

// src/common/errors/app-error.ts
var AppError = class _AppError extends Error {
  statusCode;
  isOperational;
  code;
  constructor(message, statusCode, code = "INTERNAL_ERROR", isOperational = true) {
    super(message);
    this.statusCode = statusCode;
    this.code = code;
    this.isOperational = isOperational;
    Error.captureStackTrace(this, this.constructor);
    Object.setPrototypeOf(this, _AppError.prototype);
  }
};

// src/common/errors/bad-request.error.ts
var BadRequestError = class _BadRequestError extends AppError {
  constructor(message = "Requete invalide", code = "BAD_REQUEST") {
    super(message, 400, code);
    Object.setPrototypeOf(this, _BadRequestError.prototype);
  }
};

// src/common/errors/not-found.error.ts
var NotFoundError = class _NotFoundError extends AppError {
  constructor(message = "Ressource non trouvee", code = "NOT_FOUND") {
    super(message, 404, code);
    Object.setPrototypeOf(this, _NotFoundError.prototype);
  }
};

// src/common/errors/unauthorized.error.ts
var UnauthorizedError = class _UnauthorizedError extends AppError {
  constructor(message = "Non authentifie", code = "UNAUTHORIZED") {
    super(message, 401, code);
    Object.setPrototypeOf(this, _UnauthorizedError.prototype);
  }
};

// src/common/errors/forbidden.error.ts
var ForbiddenError = class _ForbiddenError extends AppError {
  constructor(message = "Acces interdit", code = "FORBIDDEN") {
    super(message, 403, code);
    Object.setPrototypeOf(this, _ForbiddenError.prototype);
  }
};

// src/common/errors/conflict.error.ts
var ConflictError = class _ConflictError extends AppError {
  constructor(message = "Conflit de ressource", code = "CONFLICT") {
    super(message, 409, code);
    Object.setPrototypeOf(this, _ConflictError.prototype);
  }
};

// src/common/errors/payload-too-large.error.ts
var PayloadTooLargeError = class _PayloadTooLargeError extends AppError {
  constructor(message = "Message trop volumineux", code = "PAYLOAD_TOO_LARGE") {
    super(message, 413, code);
    Object.setPrototypeOf(this, _PayloadTooLargeError.prototype);
  }
};

// src/common/errors/too-many-requests.error.ts
var TooManyRequestsError = class _TooManyRequestsError extends AppError {
  constructor(message = "Trop de requetes", code = "TOO_MANY_REQUESTS") {
    super(message, 429, code);
    Object.setPrototypeOf(this, _TooManyRequestsError.prototype);
  }
};

// src/common/errors/service-unavailable.error.ts
var ServiceUnavailableError = class _ServiceUnavailableError extends AppError {
  constructor(message = "Service indisponible", code = "SERVICE_UNAVAILABLE") {
    super(message, 503, code);
    Object.setPrototypeOf(this, _ServiceUnavailableError.prototype);
  }
};

// src/common/app-source.enum.ts
var AppSource = {
  BSTOCK: "BSTOCK",
  BSTAFF: "BSTAFF",
  BDISPLAY: "BDISPLAY",
  CORE: "CORE"
};
export {
  ALL_PRODUCTS,
  ActivationStatus,
  AppError,
  AppSource,
  BadRequestError,
  BrandType,
  CURRENCY_INFO,
  ConflictError,
  Currency,
  DEFAULT_CURRENCY,
  ForbiddenError,
  MODULE_PERMISSIONS,
  NotFoundError,
  PERMISSION_MATRIX,
  PERMISSION_TO_PRODUCT,
  PRODUCT_LABELS,
  PayloadTooLargeError,
  Product,
  RoleHierarchy,
  SUPPORTED_CURRENCIES,
  ServiceUnavailableError,
  TokenType,
  TooManyRequestsError,
  UnauthorizedError,
  UserRole,
  appSourceEnum,
  auditActionEnum,
  canAccessModule,
  createAdminBrandSchema,
  createAdminCompanySchema,
  createBrandSchema,
  createCompanySchema,
  createUserSchema,
  createUserWithPasswordSchema,
  dateOrDateTimeString,
  forgotPasswordSchema,
  getAccessibleProducts,
  getUsersQuerySchema,
  hasMinimumRole,
  hasModuleAccess,
  hasPermission,
  isAdminRole,
  isoDateTimeString,
  loginSchema,
  passwordRules,
  passwordSchema,
  queryAuditLogsSchema,
  queryAuditStatsSchema,
  queryEntityHistorySchema,
  resolveCurrentCurrency,
  setPasswordSchema,
  updateAdminBrandSchema,
  updateAdminCompanySchema,
  updateBrandPayrollSettingsSchema,
  updateBrandSchema,
  updateBrandSettingsSchema,
  updateCompanyBrandingSchema,
  updateCompanySchema,
  updateUserRoleSchema,
  updateUserSchema,
  validateTokenSchema
};
//# sourceMappingURL=index.mjs.map